import { useState, useCallback } from 'react';
import {
  fetchFacesByImage,
  fetchPhoto,
  fetchPhotoMeta,
  ignoreFace,
  unignoreFace,
  unassignFace,
  removeMatchSource,
  removePersonFromPhoto,
} from '../api';
import { toList } from '../utils/url';

/**
 * Custom hook for managing face state and operations in the Edit page.
 * Handles face loading, ignore/unignore, unassign, and soft delete operations.
 *
 * @param {Object} options - Hook options
 * @param {string} options.selectedImage - Current selected image ID
 * @param {Function} options.loadPhotos - Callback to reload photos list
 * @param {Function} options.setSelectedPhoto - Setter for selected photo state
 * @param {Function} options.setMetaDoc - Setter for metadata document state
 * @param {Function} options.updatePhoto - Callback to update a single photo in the list
 * @param {Object} options.selectedPhoto - Current selected photo (for handleUnassign)
 * @returns {Object} Face management state and handlers
 */
export default function useFaceManagement({
  selectedImage,
  loadPhotos,
  setSelectedPhoto,
  setMetaDoc,
  updatePhoto,
  selectedPhoto,
}) {
  // Face state
  const [faces, setFaces] = useState([]);
  const [selectedFace, setSelectedFace] = useState(null);
  const [loadingFaces, setLoadingFaces] = useState(false);

  // Soft-deleted manual persons: shown grayed out, not included in export, single-click restore
  const [softDeletedPersons, setSoftDeletedPersons] = useState(new Set());

  /**
   * Load faces for an image, including auto-ignore of faces outside crop
   */
  const loadFaces = useCallback(async (imageId, { skipAutoIgnore = false } = {}) => {
    if (!imageId) {
      setFaces([]);
      setSelectedPhoto(null);
      return;
    }
    setLoadingFaces(true);
    try {
      // Fetch all data in parallel for faster transitions
      const [data, photo, meta] = await Promise.all([
        fetchFacesByImage(imageId, 400, 0, true),
        fetchPhoto(imageId),
        fetchPhotoMeta(imageId),
      ]);
      const fetchedFaces = toList(data);
      // Set all state in one batch so UI updates atomically
      setFaces(fetchedFaces);
      setSelectedPhoto(photo);
      setMetaDoc(meta);
      const unlabeled = fetchedFaces.find((f) => !f.person && !f.ignored);
      setSelectedFace(unlabeled || fetchedFaces[0] || null);

      // Auto-ignore faces outside the crop on load (unless explicitly skipped)
      // Skip this when user manually unignored a face - they want it visible
      if (!skipAutoIgnore) {
        // NOTE: With Option 2, both face bbox and cropRect are in full-frame space
        const cropRect =
          meta?.pipeline?.sentinel?.winner_crop_full ||
          meta?.pipeline?.sentinel?.final_crop_rect ||
          fetchedFaces[0]?.crop_rect ||
          fetchedFaces[0]?.meta?.crop_rect ||
          fetchedFaces[0]?.meta?.final_crop_rect ||
          fetchedFaces[0]?.meta?.winner_crop_full ||
          null;
        const imgW = photo?.image_width || fetchedFaces[0]?.image_width;
        const imgH = photo?.image_height || fetchedFaces[0]?.image_height;
        if (cropRect && imgW && imgH) {
          // All coordinates use XYXY format per CLAUDE.md
          const [x1, y1, x2, y2] = cropRect;
          const outside = fetchedFaces.filter((f) => {
            // Use frame_bbox for full-frame coords to compare with cropRect
            const fb = f.frame_bbox || f.bbox;
            if (!fb) return false;
            // frame_bbox is in full-frame space (XYXY), compare with cropRect
            const [bx1 = 0, by1 = 0, bx2 = 0, by2 = 0] = fb;
            const cx = (bx1 + bx2) * 0.5;
            const cy = (by1 + by2) * 0.5;
            return cx < x1 || cx > x2 || cy < y1 || cy > y2;
          });
          if (outside.length) {
            try {
              const toIgnore = outside.filter((f) => !f.ignored);
              const results = await Promise.allSettled(
                toIgnore.map((f) => ignoreFace(f.id))
              );
              // Only update UI state for faces that were successfully ignored
              const succeededIds = new Set(
                toIgnore.filter((_, i) => results[i].status === 'fulfilled').map((f) => f.id)
              );
              if (succeededIds.size > 0) {
                setFaces((prev) =>
                  prev.map((f) => (succeededIds.has(f.id) ? { ...f, ignored: true } : f))
                );
              }
              const failCount = results.filter((r) => r.status === 'rejected').length;
              if (failCount > 0) {
                console.error(`Failed to auto-ignore ${failCount}/${toIgnore.length} faces`);
              }
            } catch (e) {
              console.error('Failed to auto-ignore outside faces', e);
            }
          }
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingFaces(false);
    }
  }, [setSelectedPhoto, setMetaDoc]);

  /**
   * Ignore a face (hide from tagging workflow)
   */
  const handleIgnore = useCallback(async (face) => {
    // Optimistic: update face state immediately
    setFaces(prev => prev.map(f => f.id === face.id ? { ...f, ignored: true } : f));
    if (selectedFace?.id === face.id) {
      // Filter out the just-ignored face before searching for next candidate.
      // The `faces` closure still has the old state (ignored: false), so without
      // this filter the same face could be re-selected.
      const remaining = faces.filter(f => f.id !== face.id);
      const next = remaining.find(f => !f.person && !f.ignored)
        || remaining.find(f => !f.ignored)
        || null;
      setSelectedFace(next);
    }
    // Fire API in background
    try {
      await ignoreFace(face.id);
      loadPhotos().catch(() => {});
    } catch (e) {
      console.error('Failed to ignore face:', e);
      // Revert optimistic update
      setFaces(prev => prev.map(f => f.id === face.id ? { ...f, ignored: false } : f));
    }
  }, [selectedFace, faces, loadPhotos]);

  /**
   * Unignore a face (restore to tagging workflow)
   */
  const handleUnignore = useCallback(async (face) => {
    // Optimistic: update face state immediately
    setFaces(prev => prev.map(f => f.id === face.id ? { ...f, ignored: false } : f));
    setSelectedFace(face.id === selectedFace?.id ? { ...face, ignored: false } : selectedFace);
    // Fire API in background
    try {
      await unignoreFace(face.id);
      loadPhotos().catch(() => {});
    } catch (e) {
      console.error('Failed to restore face:', e);
      // Revert optimistic update
      setFaces(prev => prev.map(f => f.id === face.id ? { ...f, ignored: true } : f));
    }
  }, [selectedFace, loadPhotos]);

  /**
   * Unassign a face from its person (remove tag).
   * Face stays visible and untagged, ready for re-assignment.
   */
  const handleUnassign = useCallback(async (face) => {
    if (!face?.id) return;
    const removedPersonId = face.person || face.person_id;
    const updatedFace = {
      ...face,
      person: null,
      person_id: null,
      person_name: null,
      person_display: null,
      ignored: false,
    };
    // Optimistic: clear person but keep face visible (ignored: false)
    setFaces((prev) =>
      prev.map((f) => (f.id === face.id ? updatedFace : f))
    );
    // Auto-select the unassigned face so user can re-tag immediately
    setSelectedFace(updatedFace);
    // Also remove the person from selectedPhoto.persons to prevent it showing in the name bar
    if (removedPersonId && selectedPhoto?.persons) {
      const remainingFacesWithPerson = toList(faces).filter(
        (f) => f.id !== face.id && (f.person === removedPersonId || f.person_id === removedPersonId)
      );
      // Only remove from persons list if no other faces reference this person
      if (remainingFacesWithPerson.length === 0) {
        const filteredPersons = (selectedPhoto.persons || []).filter(
          (per) => per.id !== removedPersonId
        );
        updatePhoto(selectedImage, { persons: filteredPersons });
      }
    }
    // Fire API in background, then refresh for caption update
    try {
      await unassignFace(face.id);
      loadPhotos().catch(() => {});
    } catch (e) {
      console.error('Failed to unassign face', e);
    }
  }, [selectedPhoto, faces, selectedImage, loadPhotos, updatePhoto]);

  /**
   * Remove bad match source: soft-delete the poisoned source embedding and unassign person.
   * Uses same optimistic pattern as handleUnassign.
   */
  const handleRemoveMatchSource = useCallback(async (face) => {
    if (!face?.id) return;
    const removedPersonId = face.person || face.person_id;
    const updatedFace = {
      ...face,
      person: null,
      person_id: null,
      person_name: null,
      person_display: null,
      ignored: false,
    };
    // Optimistic: clear person but keep face visible
    setFaces((prev) =>
      prev.map((f) => (f.id === face.id ? updatedFace : f))
    );
    // Auto-select the unassigned face for re-tagging
    setSelectedFace(updatedFace);
    // Remove person from photo.persons if no other faces reference them
    if (removedPersonId && selectedPhoto?.persons) {
      const remainingFacesWithPerson = toList(faces).filter(
        (f) => f.id !== face.id && (f.person === removedPersonId || f.person_id === removedPersonId)
      );
      if (remainingFacesWithPerson.length === 0) {
        const filteredPersons = (selectedPhoto.persons || []).filter(
          (per) => per.id !== removedPersonId
        );
        updatePhoto(selectedImage, { persons: filteredPersons });
      }
    }
    // Fire API in background
    try {
      await removeMatchSource(face.id);
      loadPhotos().catch(() => {});
    } catch (e) {
      console.error('Failed to remove match source', e);
    }
  }, [selectedPhoto, faces, selectedImage, loadPhotos, updatePhoto]);

  /**
   * Remove a "name only" person (added without face assignment) from the photo.
   * Calls the API to persist the removal, then refreshes photo data.
   */
  const handleRemovePerson = useCallback(async (personId) => {
    if (!personId || !selectedImage) return;
    // Optimistic UI: soft-delete immediately
    setSoftDeletedPersons((prev) => {
      const next = new Set(prev);
      next.add(personId);
      return next;
    });
    try {
      await removePersonFromPhoto(selectedImage, personId);
      // Refresh selectedPhoto (including rendered_caption) and photo list
      await loadFaces(selectedImage);
      await loadPhotos();
      // Clear soft-delete since server state is now authoritative
      setSoftDeletedPersons((prev) => {
        const next = new Set(prev);
        next.delete(personId);
        return next;
      });
    } catch (e) {
      console.error('Failed to remove person', e);
      // Revert optimistic soft-delete on failure
      setSoftDeletedPersons((prev) => {
        const next = new Set(prev);
        next.delete(personId);
        return next;
      });
    }
  }, [selectedImage, loadFaces, loadPhotos]);

  /**
   * Restore a soft-deleted person (single click)
   */
  const handleRestorePerson = useCallback((personId) => {
    if (!personId) return;
    setSoftDeletedPersons((prev) => {
      const next = new Set(prev);
      next.delete(personId);
      return next;
    });
  }, []);

  /**
   * Clear soft-deleted persons (called when navigating to new image)
   */
  const clearSoftDeletedPersons = useCallback(() => {
    setSoftDeletedPersons(new Set());
  }, []);

  return {
    // State
    faces,
    setFaces,
    selectedFace,
    setSelectedFace,
    loadingFaces,
    softDeletedPersons,

    // Handlers
    loadFaces,
    handleIgnore,
    handleUnignore,
    handleUnassign,
    handleRemoveMatchSource,
    handleRemovePerson,
    handleRestorePerson,
    clearSoftDeletedPersons,
  };
}
